/**
 * Webhooks (notification-service) domain types. Shapes verified against the
 * legacy `notificationServices` + `transformationServices` (and the LIVE list
 * call `GET /notification/v1/search?&subscriberIds={id}` → `{ subscriptions }`).
 */

/** One subscribed event on a webhook; `transformationId`/`snippetId` present once a
 * customization (Monaco snippet) has been attached to that event. */
export interface WebhookEvent {
  eventType: string;
  snippetId?: string | null;
  transformationId?: string | null;
}

/** A webhook subscription (list row + detail). */
export interface Webhook {
  id: string;
  subscriberId: string;
  destinationUrl: string;
  destinationMethod: string;
  customHeaders?: Record<string, string>;
  isEnabled: boolean;
  /** undefined = never tested; true/false = last test pass/fail. */
  isTestSuccessful?: boolean;
  events: WebhookEvent[];
}

/** Envelope of `GET /notification/v1/search` (the list). */
export interface WebhooksSearchResponse {
  subscriptions: Webhook[];
}

/** The four subscribable event types (legacy static literal — not an API). */
export const WEBHOOK_EVENT_TYPES = ['OrderCreation', 'OrderUpdate', 'ShipmentCreated', 'ShipmentUpdate'] as const;

/** Callback HTTP methods offered in the form (legacy static literal). */
export const WEBHOOK_CALLBACK_METHODS = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'] as const;

/** Body for create (`POST`) / update (`PUT`) — a full webhook (id omitted on create). */
export interface WebhookInput {
  id?: string;
  subscriberId: string;
  destinationUrl: string;
  destinationMethod: string;
  customHeaders?: Record<string, string>;
  isEnabled: boolean;
  isTestSuccessful?: boolean | null;
  events: WebhookEvent[];
}

// ── Notification audit logs (the Logs card inside Webhook detail) ──
/** One row of `GET /notification/v1/{id}/auditLogs/notifications`. `resource` is a
 * JSON string that wraps `{ Notification: { EventType, ... } }`. */
export interface NotificationAuditEvent {
  id: string;
  version: number;
  userId?: string;
  action?: string;
  resource: string;
}

export interface NotificationAuditResponse {
  events: NotificationAuditEvent[];
  total?: number;
}

// ── Transformation (the Customize / Monaco snippet flow) ──
export interface TransformationSnippet {
  id?: string;
  code?: string;
}

export interface TransformationDetails {
  id?: string;
  snippets?: TransformationSnippet[];
}

/** Response of createSnippet / createTransformation. */
export interface SnippetMutationResponse {
  transformationId: string;
  snippetId: string;
}

/** Body posted when saving a customization (snippet). */
export interface CustomizationInput {
  code: string;
  creatorId: string;
  creatorType: 'admin';
  topic: string;
  transformationType: 'outbound';
  entityId: string;
}

/** Body posted to run a snippet against a sample payload (Test Code Snippet). */
export interface TransformationTestInput {
  /** Raw JSON payload string; the service deserializes it server-side. */
  payload: string;
  /** Snippet code to execute. */
  snippetCode: string;
}

/** Diagnostics for a failed/timed-out test run. */
export interface TransformationExecutionLog {
  input?: unknown;
  output?: unknown;
  errorMsg?: string | null;
  stackTrace?: string | null;
}

/** Result of a snippet test run (the unwrapped `transformationResult`). */
export interface TransformationTestResult {
  responseMessage?: string;
  /** The transformed output (already serialized) when the run succeeds. */
  transformObj?: string | null;
  executionLog?: TransformationExecutionLog | null;
  isError: boolean;
  isTimedOut: boolean;
  httpStatusCode?: number;
  durationMs?: number;
}

/** Envelope returned by the transformation test endpoint. */
export interface TransformationTestResponse {
  transformationResult: TransformationTestResult;
}
